import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { AssignTaskListsPayload } from '@gokrew/models/employee-boarding';
import {
  assignTaskListsToFlow,
  createEmployeeFlow,
  listEmployeeFlows,
} from '@/api/employee-boarding';
import { getTaskListById } from '@/api/task-lists';
import { OnboardingWorkflowToast } from '@/components/OnboardingWorkflowToast';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useAuth } from '@/context/AuthContext';
import type { TaskEntity } from '@/types/task-lists';

const ONBOARDING = 'onboarding';

/** Long enough to reach the toast's link (View onboarding, Retry). */
const SUCCESS_TOAST_MS = 8000;
const FAILURE_TOAST_MS = 15000;

const flatten = (tasks: TaskEntity[]): TaskEntity[] =>
  tasks.flatMap((task) => [task, ...flatten(task.children ?? [])]);

/** The employee's onboarding tab. */
export const onboardingTabPath = (employeeId: string) =>
  `/employees/detail/${employeeId}/onboarding`;

export interface StartOnboardingWorkflowInput {
  employeeId: string;
  /** The onboarding task-list template to run in full. */
  taskListId: string;
  /** The template's name, for the toasts. */
  workflowName: string;
  /** Without one, the flow's due dates count from today. */
  hasJoiningDate: boolean;
}

/**
 * The employee's onboarding flow: the one already running (a retry after a
 * half-done start), or a new one. A flow's due dates count from the joining
 * date, so an employee saved without one is anchored on today, the day they
 * were added. Looking first keeps a retry from hitting the create 409, which
 * the API client would toast.
 */
const openOnboardingFlow = async (
  companyId: string,
  employeeId: string,
  hasJoiningDate: boolean
) => {
  const running = () =>
    listEmployeeFlows(companyId, employeeId, ONBOARDING, {
      status: 'in_progress',
    }).then(({ data }) => data[0]?.id);
  const existing = await running();
  if (existing) return existing;
  try {
    const { data } = await createEmployeeFlow(
      companyId,
      employeeId,
      ONBOARDING,
      hasJoiningDate
        ? {}
        : { anchorDate: new Date().toLocaleDateString('en-CA') }
    );
    return data.id;
  } catch (error) {
    // Started at the same moment elsewhere: use that one.
    if (!isAxiosError(error) || error.response?.status !== 409) throw error;
    const flowId = await running();
    if (!flowId) throw error;
    return flowId;
  }
};

/** Opens the flow and assigns every task of the template to it. */
const runOnboardingWorkflow = async (
  companyId: string,
  { employeeId, taskListId, hasJoiningDate }: StartOnboardingWorkflowInput
) => {
  // The template first: if it is gone, no empty flow is left behind.
  const { data: template } = await getTaskListById(
    companyId,
    ONBOARDING,
    taskListId
  );
  const flowId = await openOnboardingFlow(
    companyId,
    employeeId,
    hasJoiningDate
  );
  const payload: AssignTaskListsPayload = {
    taskLists: [
      {
        taskListTemplateId: template.id,
        tasks: flatten(template.tasks).map((task) => ({
          taskTemplateId: task.id,
          ...(task.assigneeType !== 'employee_dynamic' &&
            task.targetId && { targetId: task.targetId }),
        })),
      },
    ],
  };
  await assignTaskListsToFlow(
    companyId,
    employeeId,
    ONBOARDING,
    flowId,
    payload
  );
};

/**
 * Starts an onboarding workflow for a just-created employee with every task
 * of its template, the same as picking all of it in "Add tasks" on the
 * employee's Onboarding tab. Used by the Add employee form, from the
 * Employees page and from a hire in recruiting alike.
 *
 * The employee is already saved when this runs, so a failure never undoes
 * them: the toast says onboarding did not start and offers Retry. Success
 * names the workflow and links to the employee's Onboarding tab.
 */
export const useStartOnboardingWorkflow = () => {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Employee);
  const navigate = useNavigate();

  return useCallback(
    function start(input: StartOnboardingWorkflowInput) {
      if (!companyId) return;
      const name = input.workflowName;
      runOnboardingWorkflow(companyId, input)
        .then(() =>
          toast.success(
            (shown) => (
              <OnboardingWorkflowToast
                message={t('employee.onboarding_workflow.started', { name })}
                actionLabel={t('employee.onboarding_workflow.view')}
                onAction={() => {
                  toast.dismiss(shown.id);
                  navigate(onboardingTabPath(input.employeeId));
                }}
              />
            ),
            { duration: SUCCESS_TOAST_MS }
          )
        )
        .catch(() =>
          toast.error(
            (shown) => (
              <OnboardingWorkflowToast
                message={t('employee.onboarding_workflow.failed', { name })}
                actionLabel={t('employee.onboarding_workflow.retry')}
                onAction={() => {
                  toast.dismiss(shown.id);
                  start(input);
                }}
              />
            ),
            { duration: FAILURE_TOAST_MS }
          )
        );
    },
    [companyId, t, navigate]
  );
};
